import type { GameData, Question } from '@/features/gameSession';

type ProgressLevel = {
	required_stars: number;
	level_image?: string;
	level_name?: string;
	level_title?: string;
};

export type MeasurementGameLevelProgress = {
	stars_earned: number;
	level_stars_earned: number;
	current_level: ProgressLevel;
	level_completed?: ProgressLevel | null;
	user_learning_path_section?: GameData;
};

export enum MeasurementGameScreen {
	Start = 'start',
	Game = 'game',
	End = 'end',
}

export type MeasurementGameData = Omit<GameData, 'questions' | 'title' | 'type'> & {
	title?: string;
	type?: 'MeasurementGame';
	firstWorld?: WorldKey | null;
	questions?: UnitConversionQuestion[];
};

export const WORLD_KEYS = ['cooking', 'dino', 'ruler', 'slime'] as const;
export type WorldKey = (typeof WORLD_KEYS)[number];

export type LevelPoint = {
	x: number;
	y: number;
};

// Coins and hearts; `value` defaults to 1.
export type PickupData = LevelPoint & { value?: number };

export type NpcData = {
	line?: string;
	game_message_id?: number;
	encounters?: string[] | null;
	fact?: string[] | null;
	not_enough_stars?: string[] | null;
	current_fact_threshold?: number;
	next_fact_threshold?: number;
};

export type NpcFactUpdateHandler = (data: { measurement_game_last_message_seen_id: number }) => void;

export type NpcSession = {
	encounterSeen: boolean;
	factShown: boolean;
};

export type PlatformData = LevelPoint & {
	width?: number;
	height?: number;
	label?: string;
	door?: { type: 'start' | 'target'; title: string };
	npc?: NpcData;
};

export type QuestionMessages = {
	alien_first_tap?: string[] | null;
	alien_repeated_tap?: string[] | null;
	incorrect_answer_first?: string[] | null;
	incorrect_answer_repeated?: string[] | null;
};

export type LevelGameData = {
	messages?: QuestionMessages | null;
	name: string;
	difficulty: 'easy' | 'hard' | string;
	number_of_steps: number;
	platforms: PlatformData[];
	coins: PickupData[];
	hearts: PickupData[];
	hazards: LevelPoint[];
};

export type UnitConversionQuestion = Partial<Question> & {
	id: number;
	type: 'Question::UnitConversion';
	question_display: string;
	answers: {
		id: number | string;
		is_correct: boolean;
		answer_display: string;
	}[];
	game: LevelGameData;
};

export type LevelData = {
	messages?: QuestionMessages | null;
	id: number;
	name: string;
	difficulty: string;
	numberOfSteps: number;
	questionDisplay: string;
	correctAnswer: string;
	world?: WorldKey;
	platforms: PlatformData[];
	coins: PickupData[];
	hearts: PickupData[];
	hazards: LevelPoint[];
};
